import React, { useState, useEffect } from 'react';
import useAudioRecorder from '../hooks/useAudioRecorder';
import ClariMeLogo from './ClariMeLogo';
import Waveform from './Waveform';
import { MicIcon, CloseIcon } from './icons';

const RecordingScreen = ({ onComplete, onCancel, recover = null }) => {
  const {
    isRecording,
    isPaused,
    duration,
    formattedDuration,
    audioLevel,
    audioBlob,
    error,
    isNativeRecording,
    interruptionStatus,
    startRecording,
    stopRecording,
    pauseRecording,
    resumeRecording
  } = useAudioRecorder({ recover });

  const [hasStarted, setHasStarted] = useState(!!recover);
  const [wasBackgrounded, setWasBackgrounded] = useState(false);
  const [showRecoveredBanner, setShowRecoveredBanner] = useState(!!recover);

  // Start recording when component mounts (skip if recovering — the native
  // recorder is already running and useAudioRecorder rehydrates state).
  useEffect(() => {
    if (!hasStarted) {
      startRecording();
      setHasStarted(true);
    }
  }, [hasStarted, startRecording]);

  // Auto-hide the "Recording recovered" banner after a few seconds
  useEffect(() => {
    if (!showRecoveredBanner) return;
    const t = setTimeout(() => setShowRecoveredBanner(false), 5000);
    return () => clearTimeout(t);
  }, [showRecoveredBanner]);

  // When recording stops and we have audio, pass it along
  useEffect(() => {
    if (audioBlob && !isRecording && hasStarted) {
      onComplete({
        // durationSeconds is upload metadata only — visit length is never
        // shown alongside a summary (see shareController.js note)
        durationSeconds: duration,
        audioBlob: audioBlob,
      });
    }
  }, [audioBlob, isRecording, hasStarted, duration, onComplete]);

  // Track when app returns from background during native recording
  useEffect(() => {
    if (!isNativeRecording) return;

    const handleVisibility = () => {
      if (document.visibilityState === 'visible') {
        setWasBackgrounded(true);
        // Clear the badge after a few seconds
        setTimeout(() => setWasBackgrounded(false), 3000);
      }
    };
    document.addEventListener('visibilitychange', handleVisibility);
    return () => document.removeEventListener('visibilitychange', handleVisibility);
  }, [isNativeRecording]);

  const handlePauseResume = () => {
    if (isPaused) {
      resumeRecording();
    } else {
      pauseRecording();
    }
  };

  const handleStop = () => {
    stopRecording();
  };

  const handleCancel = () => {
    stopRecording();
    onCancel();
  };

  // Show error state if mic access was denied
  if (error) {
    return (
      <div className="fixed inset-0 flex flex-col bg-gradient-to-b from-slate-900 via-slate-800 to-slate-900" style={{ height: '100dvh' }}>
        <div className="pt-safe px-4 pb-2 flex items-center justify-between flex-shrink-0">
          <button onClick={onCancel} className="p-2 -ml-2 rounded-full active:bg-white/10 text-white/60">
            <CloseIcon />
          </button>
          <div className="w-10" />
          <div className="w-10" />
        </div>

        <div className="flex-1 flex flex-col items-center justify-center px-6 min-h-0">
          <div className="w-16 h-16 bg-red-500/20 rounded-xl flex items-center justify-center mb-5">
            <MicIcon className="w-8 h-8 text-red-400" />
          </div>

          <h2 className="text-lg font-semibold text-white mb-2">
            {error.includes('Permission') || error.includes('NotAllowed') || error.includes('denied')
              ? 'Microphone Access Required'
              : 'Recording Problem'
            }
          </h2>
          <p className="text-white/60 text-center mb-6 max-w-xs text-sm">
            {error.includes('Permission') || error.includes('NotAllowed') || error.includes('denied')
              ? "Please allow microphone access in your device settings to record your appointment."
              : error
            }
          </p>

          <button
            onClick={onCancel}
            className="px-6 py-3 bg-white/10 text-white rounded-xl font-medium active:bg-white/20 transition-colors"
          >
            Go Back
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 flex flex-col bg-gradient-to-b from-slate-900 via-slate-800 to-slate-900" style={{ height: '100dvh' }}>
      {/* Header */}
      <div className="pt-safe px-4 pb-2 flex items-center justify-between flex-shrink-0">
        <button onClick={handleCancel} className="p-2 -ml-2 rounded-full active:bg-white/10 text-white/60">
          <CloseIcon />
        </button>
        <div className="flex items-center gap-2 bg-white/10 px-3 py-1.5 rounded-full">
          <span className={`w-2 h-2 rounded-full ${isPaused ? 'bg-amber-500' : 'bg-red-500 animate-pulse'}`} />
          <span className="text-sm font-medium text-white/90">
            {isPaused ? (interruptionStatus === 'began' ? 'Interrupted' : 'Paused') : 'Recording'}
          </span>
        </div>
        <div className="w-10" />
      </div>

      {/* Recovery banner — shown when this screen mounted in recover mode */}
      {showRecoveredBanner && (
        <div className="mx-4 mb-2 rounded-xl bg-teal-500/15 border border-teal-400/30 px-3 py-2.5 text-sm text-teal-100 flex items-start gap-2 flex-shrink-0">
          <svg className="w-4 h-4 text-teal-300 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.857-9.809a.75.75 0 00-1.214-.882l-3.483 4.79-1.88-1.88a.75.75 0 10-1.06 1.061l2.5 2.5a.75.75 0 001.137-.089l4-5.5z" clipRule="evenodd" />
          </svg>
          <div>Recording recovered. Tap stop when your visit is done — your audio is still being captured.</div>
        </div>
      )}

      {/* Interruption banner — shown when iOS pauses us for a call/Siri/alarm */}
      {interruptionStatus === 'began' && (
        <div className="mx-4 mb-2 rounded-xl bg-amber-500/15 border border-amber-400/30 px-3 py-2.5 text-sm text-amber-100 flex items-start gap-2 flex-shrink-0">
          <svg className="w-4 h-4 text-amber-300 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M8.485 2.495c.673-1.167 2.357-1.167 3.03 0l6.28 10.875c.673 1.167-.17 2.625-1.516 2.625H3.72c-1.347 0-2.189-1.458-1.515-2.625L8.485 2.495zM10 5a.75.75 0 01.75.75v3.5a.75.75 0 01-1.5 0v-3.5A.75.75 0 0110 5zm0 9a1 1 0 100-2 1 1 0 000 2z" clipRule="evenodd" />
          </svg>
          <div>Recording paused by the system. Tap resume when ready, or stop to save what was captured.</div>
        </div>
      )}

      {/* Content — locked to viewport */}
      <div className="flex-1 flex flex-col items-center justify-center px-6 min-h-0">
        <div className="mb-3">
          <ClariMeLogo size="large" variant="dark" />
        </div>

        {isNativeRecording ? (
          /* Native recording: pulse animation instead of real-time waveform */
          <div className="h-16 flex items-center justify-center mb-2">
            <div className="flex items-center gap-1.5">
              {[0, 1, 2, 3, 4, 5, 6].map((i) => (
                <div
                  key={i}
                  className={`w-1 rounded-full bg-gradient-to-t from-teal-400 to-cyan-400 ${isPaused ? '' : 'animate-pulse'}`}
                  style={{
                    height: isPaused ? '8px' : `${12 + Math.sin(i * 0.9) * 10}px`,
                    animationDelay: `${i * 150}ms`,
                    animationDuration: '1.5s',
                    opacity: isPaused ? 0.4 : 0.8,
                  }}
                />
              ))}
            </div>
          </div>
        ) : (
          <Waveform isRecording={isRecording && !isPaused} audioLevel={audioLevel} />
        )}

        {/* Audio level indicator — web only */}
        {!isNativeRecording && (
          <div className="flex items-center gap-2 mt-2 mb-3">
            <div className="text-xs text-white/40">Mic Level:</div>
            <div className="w-28 h-1.5 bg-white/10 rounded-full overflow-hidden">
              <div
                className="h-full bg-gradient-to-r from-teal-400 to-cyan-400 transition-all duration-75"
                style={{ width: `${Math.min(100, audioLevel * 300)}%` }}
              />
            </div>
            <div className="text-xs text-white/40 w-8">{Math.round(audioLevel * 100)}%</div>
          </div>
        )}

        {/* Native recording: background-safe indicator */}
        {isNativeRecording && !isPaused && (
          <div className="flex items-center gap-1.5 mt-2 mb-3">
            <svg className="w-3.5 h-3.5 text-teal-400" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.857-9.809a.75.75 0 00-1.214-.882l-3.483 4.79-1.88-1.88a.75.75 0 10-1.06 1.061l2.5 2.5a.75.75 0 001.137-.089l4-5.5z" clipRule="evenodd" />
            </svg>
            <span className="text-xs text-teal-400/80">
              {wasBackgrounded ? 'Still recording' : 'Safe to lock screen'}
            </span>
          </div>
        )}

        <div className="text-5xl font-light text-white my-6 tabular-nums tracking-tight">
          {formattedDuration}
        </div>

        <div className="flex items-center gap-8 mb-4">
          <button
            onClick={handlePauseResume}
            className="w-14 h-14 rounded-full bg-white/10 backdrop-blur border border-white/20 flex items-center justify-center active:bg-white/20 transition-all"
          >
            {isPaused ? (
              <svg className="w-6 h-6 text-white ml-0.5" fill="currentColor" viewBox="0 0 24 24"><path d="M8 5v14l11-7z"/></svg>
            ) : (
              <svg className="w-6 h-6 text-white" fill="currentColor" viewBox="0 0 24 24"><path d="M6 19h4V5H6v14zm8-14v14h4V5h-4z"/></svg>
            )}
          </button>

          <button
            onClick={handleStop}
            className="w-18 h-18 rounded-full bg-gradient-to-br from-red-500 to-rose-600 text-white shadow-lg shadow-red-500/30 flex items-center justify-center active:scale-95 transition-transform"
            style={{ width: '72px', height: '72px' }}
          >
            <div className="w-6 h-6 bg-white rounded-md" />
          </button>

          <div className="w-14" />
        </div>

        <p className="text-white/60 text-sm font-medium">
          {isPaused ? 'Tap play to resume' : 'Stop when your visit is done'}
        </p>
      </div>

      {/* Safe area padding */}
      <div className="pb-6 flex-shrink-0" />
    </div>
  );
};

export default RecordingScreen;
